import { ToFlareXMLOptions } from '@common/flare/types/to-flare-xml-options.type';
import { TrackedChangesExporter } from '@common/flare/util/tracked-changes-exporter';
import { AttributeSpec, Attrs, Fragment, Mark, ProseMirrorNode } from 'prosemirror-model';

export interface NodeToFlareXMLOptions {
  /**
   * The children to render instead of the children on the node.
   * If the child is a ProseMirrorNode then it is rendered with toFlareXML.
   * If the child is a string then it is used as the rendered XML.
   */
  children?: (ProseMirrorNode | string)[];
  /**
   * Render the given XML instead of building the XML from the tagName, attributes, and children.
   * The node's marks will still be rendered around the XML.
   */
  xml?: string;
}

export interface FlareXMLOutputSpec {
  attrs?: Dictionary<string>;
  tagName: string;
}

/**
 * Converts a ProseMirror Fragment into an array of ProseMirror nodes.
 * @param fragment The fragment to convert.
 * @returns An array of the fragment's child nodes.
 */
export function fragmentToArray(fragment: Fragment): ProseMirrorNode[] {
  const nodes: ProseMirrorNode[] = [];
  fragment.forEach(node => nodes.push(node));
  return nodes;
}

/**
 * Default serialization for ProseMirror nodes that do not define a custom `toFlareXML` method.
 * Delegates to {@link nodeToFlareXML} using the node's `tagName` spec or type name.
 * @param node The ProseMirror node to serialize.
 * @param options Serialization options including tracked changes and clipboard export settings.
 * @returns The Flare XML string for the node.
 */
export function defaultToFlareXML(node: ProseMirrorNode, options: ToFlareXMLOptions): string {
  return nodeToFlareXML(node, options, node.type.spec.tagName ?? node.type.name);
}

/**
 * Encodes an XML attribute value by escaping special characters (`&`, `"`, `<`, `>`).
 * @param attr The attribute value to encode.
 * @returns The encoded attribute value safe for use in XML attributes.
 */
export function encodeAttr(attr: string): string {
  return attr
    .replace(/&/g, '&amp;') // Replace the & first
    // .replace(/'/g, '&apos;') // Not necessary because the attribute quotes are always double quotes
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

const textReplacements = {
  '\xA0': '&#160;',
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;'
};

/**
 * Encodes XML text content by escaping special characters (`<`, `>`, `&`, non-breaking spaces).
 * @param text The text content to encode.
 * @returns The encoded text safe for use in XML text nodes.
 */
export function encodeText(text: string): string {
  return text.replace(/[<>&\xA0]/g, match => textReplacements[match]);
}

/**
 * Serializes a ProseMirror node to Flare XML. Wraps the output in the node's marks, builds the
 * opening/closing tags with attributes, and recursively serializes child content.
 * @param node The ProseMirror node to serialize.
 * @param options Serialization options including tracked changes and clipboard export settings.
 * @param tagName The XML tag name to use for the element.
 * @param nodeOptions Optional overrides for children or raw XML content.
 * @returns The Flare XML string for the node.
 */
export function nodeToFlareXML(node: ProseMirrorNode, options: ToFlareXMLOptions, tagName: string, nodeOptions?: NodeToFlareXMLOptions): string {
  let xml = '';

  // Start by wrapping the node in its marks
  const markXMLSpecs: FlareXMLOutputSpec[] = [];
  // Open each mark
  for (let i = 0; i < node.marks.length; i += 1) {
    const mark = node.marks[i];
    const xmlSpec = mark.type.spec.toFlareXML(mark, options);
    if (xmlSpec) {
      markXMLSpecs.push(xmlSpec);
      xml += `<${xmlSpec.tagName}`;
      xml += attrsToFlareXML(xmlSpec.attrs, mark.type.spec.attrs, options, mark.type.name, mark);
      xml += '>';
    }
  }

  if (nodeOptions?.xml) {
    xml += nodeOptions.xml;
  } else if (node.isText) {
    xml += encodeText(node.text);
  } else {
    // Open the tag
    xml += `<${tagName}`;

    // Add the attributes
    xml += attrsToFlareXML(node.attrs, node.type.spec.attrs, options, node.type.name, node);

    if (node.type.spec.isVoid) {
      // Void tags are self-closing
      xml += ' />';
    } else {
      // First try getting the content from the contentToFlareXML method as it overrides the default behavior
      let content = node.type.spec.contentToFlareXML?.(node, options, nodeOptions);

      // If the node's contentToFlareXML method didn't return a string then use the default contentToFlareXML method to get the node content
      if (typeof content !== 'string') {
        content = contentToFlareXML(node, options, nodeOptions);
      }

      if (!content && node.type.spec.isVoidWhenEmpty) {
        // If the node is empty and isVoidWhenEmpty is true then render it as a self-closing tag
        xml += ' />';
      } else {
        xml += '>';
        // Add the content
        xml += content;
        // Close the tag
        xml += `</${tagName}>`;
      }
    }
  }

  // Close each mark
  for (let i = markXMLSpecs.length - 1; i >= 0; i -= 1) {
    xml += `</${markXMLSpecs[i].tagName}>`;
  }

  return xml;
}

/** Checks if a value is a plain object (dictionary), not an array or primitive. */
function isDictionary(value: any): value is Dictionary<any> {
  return value && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Converts ProseMirror node/mark attributes to a flat dictionary of string values for XML export.
 * Skips attributes marked with `skipExport`, applies custom `toFlareXML` transformations from
 * attribute specs, handles `exportedName` remapping, and filters out null/undefined values.
 * @param attrs The ProseMirror attributes to convert.
 * @param nodeOrMark The node or mark that owns the attributes, used for spec lookups.
 * @param options Serialization options including tracked changes exporter.
 * @returns A dictionary of attribute name/value pairs ready for XML output.
 */
export function attrsToDictionary(attrs: Attrs, nodeOrMark: ProseMirrorNode | Mark, options: ToFlareXMLOptions): Dictionary<string> {
  const attributes: Dictionary<string> = {};
  const attrsSpecs = nodeOrMark.type.spec.attrs;

  options.trackedChangesExporter = options.trackedChangesExporter ?? new TrackedChangesExporter();

  for (let [attributeName, value] of Object.entries(attrs)) {
    if (attrsSpecs?.[attributeName]?.skipExport) {
      continue;
    }

    if (attrsSpecs?.[attributeName]?.toFlareXML) {
      const newValue = attrsSpecs[attributeName].toFlareXML(value, options, nodeOrMark);
      if (isDictionary(newValue)) {
        Object.assign(attributes, attrsToDictionary(newValue, nodeOrMark, options));
        continue;
      } else {
        value = newValue;
      }
    }

    if (typeof value !== 'undefined' && value !== null) {
      if (typeof value !== 'string' && typeof value !== 'number') {
        throw new Error(`Attribute value not a string. Node: ${nodeOrMark.type.name}, Attribute: ${attributeName}, Value: ${value}`);
      }

      if (value === '' && attributeName === 'class') {
        continue;
      }

      if (attrsSpecs?.[attributeName]?.exportedName) {
        attributeName = attrsSpecs[attributeName].exportedName;
      }

      attributes[attributeName] = value.toString();
    }
  }

  return attributes;
}

/**
 * Serializes ProseMirror node/mark attributes to a Flare XML attribute string (e.g., ` attr="value"`).
 * Applies custom `toFlareXML` transformations, skips `skipExport` attributes, encodes values,
 * and handles `exportedName` remapping.
 * @param attributes The attributes dictionary to serialize.
 * @param attrsSpecs The attribute specs from the node/mark type.
 * @param options Serialization options including tracked changes and clipboard export settings.
 * @param nodeName The name of the node or mark type, used for error messages.
 * @param nodeOrMark The node or mark instance, passed to custom `toFlareXML` attribute transformers.
 * @returns A string of XML attributes (with leading spaces), e.g., ` foo="bar" baz="qux"`.
 */
export function attrsToFlareXML(attributes: Dictionary<string | number>, attrsSpecs: Dictionary<AttributeSpec>, options: ToFlareXMLOptions, nodeName: string, nodeOrMark: ProseMirrorNode | Mark): string {
  let xml = '';

  if (attributes) {
    for (let [attributeName, value] of Object.entries(attributes)) {
      if (attrsSpecs?.[attributeName]?.skipExport) {
        continue;
      }

      const flareAttr = attrToFlareValue(attributeName, value, attrsSpecs, nodeName, nodeOrMark, options);
      if (flareAttr?.hasOwnProperty('xml')) {
        xml += flareAttr.xml;
        continue;
      } else if (flareAttr?.hasOwnProperty('newValue')) {
        value = flareAttr.newValue;
      }

      if (typeof value !== 'undefined' && value !== null) {
        if (typeof value !== 'string' && typeof value !== 'number') {
          throw new Error(`Attribute value not a string. Node: ${nodeName}, Attribute: ${attributeName}, Value: ${value}`);
        }

        if (value === '' && attributeName === 'class') {
          continue;
        }

        if (attrsSpecs?.[attributeName]?.exportedName) {
          attributeName = attrsSpecs[attributeName].exportedName;
        }

        xml += ` ${attributeName}="${encodeAttr(value.toString())}"`;
      }
    }
  }

  return xml;
}

/**
 * Attempt to transform the attribute to Flare XML.
 * @param attributeName the xml attribute to transform
 * @param value the value of the xml attribute
 * @param attrsSpecs the node's attribute specs
 * @param nodeName the name of the node or mark
 * @param nodeOrMark the node or mark
 * @param options trackedChangesExporter?: TrackedChangesExporter & exportForClipboard?: boolean
 * @returns Undefined if toFlareXML doesn't exist, the xml as a string if the toFlareXml returns a dictionary, or the xml value as a string.
 */
export function attrToFlareValue(attributeName: string, value: string | number, attrsSpecs: Dictionary<AttributeSpec>, nodeName: string, nodeOrMark: ProseMirrorNode | Mark, options: ToFlareXMLOptions = {}): { xml?: string, newValue?: string } {
  if (attrsSpecs?.[attributeName]?.toFlareXML) {
    const newValue = attrsSpecs[attributeName].toFlareXML(value, options, nodeOrMark);
    if (isDictionary(newValue)) {
      return { xml: attrsToFlareXML(newValue, attrsSpecs, options, nodeName, nodeOrMark) };
    } else {
      return { newValue: newValue as string };
    }
  }
}

/**
 * Serializes the child content of a ProseMirror node to Flare XML.
 * If `nodeOptions.children` is provided, those are serialized instead of the node's actual children.
 * Each child is serialized using its `toFlareXML` spec method or the {@link defaultToFlareXML} fallback.
 * @param node The parent ProseMirror node whose content to serialize.
 * @param options Serialization options including tracked changes and clipboard export settings.
 * @param nodeOptions Optional overrides, including custom children to render.
 * @returns The Flare XML string for the node's content.
 */
export function contentToFlareXML(node: ProseMirrorNode, options: ToFlareXMLOptions, nodeOptions?: NodeToFlareXMLOptions): string {
  let xml = '';

  // If the options specify children then use them for the content
  if (Array.isArray(nodeOptions?.children)) {
    for (let i = 0; i < nodeOptions.children.length; i += 1) {
      const child = nodeOptions.children[i];
      if (typeof child === 'string') {
        xml += child;
      } else {
        xml += (child.type.spec.toFlareXML ?? defaultToFlareXML)(child, options);
      }
    }
  }
  // Else use the node's children for the content
  else {
    for (let i = 0; i < node.childCount; i += 1) {
      const child = node.child(i);
      xml += (child.type.spec.toFlareXML ?? defaultToFlareXML)(child, options)
    }
  }

  return xml;
}
